/**
 * Lot 2.2 — « Vous avez déjà votre programme ? » : le formateur dépose un PDF ou un Word, l'assistant IA le convertit,
 * puis une fenêtre présente CHAQUE champ proposé avec une case « reprendre ». Rien n'est enregistré ici : les champs
 * cochés remplissent le formulaire, que le formateur relit et enregistre lui-même (validation humaine).
 * Sans IA configurée, le dépôt est désactivé et l'écran explique pourquoi.
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FileUp } from "lucide-react";
import type { ModuleParcours } from "@/domaine/pedagogie/parcours";
import { api, ErreurApi, heuresFr, type ProgrammeImporte } from "../api";
import { Alerte, Bouton, Carte, DepotFichier, Modale } from "../ui/base";
import { AlerteIaIndisponible } from "./AssistantIa";

export interface ChampsImportes {
  formation_titre?: string;
  formation_niveau?: string;
  public_vise?: string;
  formation_prerequis?: string;
  heures?: string;
  nb_modules?: string;
  formation_objectifs?: string;
  programme?: string;
  modules?: ModuleParcours[];
}

type Cle = "formation_titre" | "formation_niveau" | "public_vise" | "formation_prerequis" | "heures" | "parcours";

export function ImportProgramme({ iaDisponible, iaChargement, heures, actuel, appliquer }: { iaDisponible: boolean; iaChargement: boolean; heures: string; actuel: { formation_titre: string; formation_niveau: string; public_vise: string; formation_prerequis: string; heures: string; nb_modules_actuels: number }; appliquer: (c: ChampsImportes) => void }) {
  const [proposition, setProposition] = useState<ProgrammeImporte | null>(null);
  const [choix, setChoix] = useState<Record<Cle, boolean>>({ formation_titre: false, formation_niveau: false, public_vise: false, formation_prerequis: false, heures: false, parcours: true });
  const importer = useMutation({
    mutationFn: (f: File) => api.fichier<ProgrammeImporte>("/ia/import-programme", f, heures ? { heures } : {}),
    onSuccess: (p) => {
      // Par défaut, on ne propose de remplacer que ce qui est vide ; le parcours (modules, objectifs, programme) est coché.
      setChoix({
        formation_titre: !actuel.formation_titre.trim(),
        formation_niveau: !actuel.formation_niveau.trim() && p.formation_niveau !== "",
        public_vise: !actuel.public_vise.trim() && p.public_vise !== "",
        formation_prerequis: !actuel.formation_prerequis.trim() && p.formation_prerequis !== "",
        heures: !actuel.heures.trim() && p.formation_duree_heures_total !== null,
        parcours: true,
      });
      setProposition(p);
    },
  });
  const erreurs = importer.error instanceof ErreurApi && Array.isArray(importer.error.details?.erreurs) ? importer.error.details.erreurs : [];

  const lignes: Array<{ cle: Cle; libelle: string; valeur: string; actuelle: string }> = proposition
    ? [
        { cle: "formation_titre", libelle: "Intitulé", valeur: proposition.formation_titre, actuelle: actuel.formation_titre },
        { cle: "formation_niveau", libelle: "Niveau", valeur: proposition.formation_niveau, actuelle: actuel.formation_niveau },
        { cle: "heures", libelle: "Durée totale", valeur: proposition.formation_duree_heures_total === null ? "" : heuresFr(proposition.formation_duree_heures_total), actuelle: actuel.heures ? `${actuel.heures} h` : "" },
        { cle: "public_vise", libelle: "Public visé", valeur: proposition.public_vise, actuelle: actuel.public_vise },
        { cle: "formation_prerequis", libelle: "Prérequis", valeur: proposition.formation_prerequis, actuelle: actuel.formation_prerequis },
      ]
    : [];

  const reprendre = () => {
    if (!proposition) return;
    const p = proposition;
    appliquer({
      ...(choix.formation_titre ? { formation_titre: p.formation_titre } : {}),
      ...(choix.formation_niveau ? { formation_niveau: p.formation_niveau } : {}),
      ...(choix.public_vise ? { public_vise: p.public_vise } : {}),
      ...(choix.formation_prerequis ? { formation_prerequis: p.formation_prerequis } : {}),
      ...(choix.heures && p.formation_duree_heures_total !== null ? { heures: String(p.formation_duree_heures_total).replace(".", ",") } : {}),
      ...(choix.parcours ? { modules: p.modules, nb_modules: String(p.modules.length), formation_objectifs: p.formation_objectifs, programme: p.programme } : {}),
    });
    setProposition(null);
  };

  return (
    <Carte className="p-4">
      <p className="flex items-center gap-2 font-semibold">
        <FileUp className="size-4 text-accent" aria-hidden /> Vous avez déjà votre programme ?
      </p>
      <p className="mt-1 text-[13px] text-encre-2">Déposez-le (PDF ou Word .docx, 10 Mo au plus) : l'assistant le convertit en modules, objectifs et contenus, sans en changer le fond. Vous choisissez ensuite ce que vous reprenez. Le fichier n'est pas conservé.</p>
      {!iaChargement && !iaDisponible && (
        <div className="mt-3">
          <AlerteIaIndisponible />
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <DepotFichier libelle={importer.isPending ? "Lecture du programme…" : "Importer mon programme"} accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" enCours={importer.isPending} disabled={!iaDisponible} title={!iaDisponible ? "L'assistant IA n'est pas configuré" : undefined} deposer={(f) => importer.mutate(f)} />
        {importer.isPending && <span className="text-[12.5px] text-encre-3">Environ une minute.</span>}
      </div>
      {importer.error && (
        <div className="mt-3">
          <Alerte ton="danger" titre={importer.error.message}>
            {erreurs.length > 0 && (
              <ul className="list-disc pl-4">
                {erreurs.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            )}
          </Alerte>
        </div>
      )}

      <Modale ouverte={proposition !== null} fermer={() => setProposition(null)} titre="Programme lu par l'assistant : que reprenez-vous ?" large>
        {proposition && (
          <div className="space-y-4">
            <Alerte ton="attention" titre={`Proposition tirée de « ${proposition.nom_fichier} »`}>
              Vérifiez chaque champ : l'assistant peut se tromper. Ce que vous cochez remplit le formulaire ; rien n'est enregistré tant que vous n'avez pas cliqué sur « Enregistrer ».
            </Alerte>
            <ul className="divide-y divide-trait rounded-md border border-trait">
              {lignes.map((l) => (
                <li key={l.cle} className="p-3">
                  <label className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1 size-4 shrink-0 accent-[var(--color-accent)]" checked={choix[l.cle]} disabled={!l.valeur} onChange={(e) => setChoix({ ...choix, [l.cle]: e.target.checked })} />
                    <span className="min-w-0 text-sm">
                      <span className="font-semibold">{l.libelle}</span>
                      <span className="block break-words whitespace-pre-line">{l.valeur || <em className="text-encre-3">non trouvé dans le document</em>}</span>
                      {l.actuelle && l.valeur && l.actuelle !== l.valeur && <span className="mt-0.5 block text-[12.5px] text-encre-3">Actuellement : {l.actuelle} — remplacé si vous cochez.</span>}
                    </span>
                  </label>
                </li>
              ))}
              <li className="p-3">
                <label className="flex items-start gap-3">
                  <input type="checkbox" className="mt-1 size-4 shrink-0 accent-[var(--color-accent)]" checked={choix.parcours} onChange={(e) => setChoix({ ...choix, parcours: e.target.checked })} />
                  <span className="min-w-0 text-sm">
                    <span className="font-semibold">Parcours : {proposition.modules.length} module(s), objectifs et programme détaillé</span>
                    <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-[13px]">
                      {proposition.modules.map((m, i) => (
                        <li key={i}>
                          {m.titre} <span className="chiffres text-encre-3">({heuresFr(m.duree_heures)})</span>
                        </li>
                      ))}
                    </ol>
                    {proposition.durees_reparties && <span className="mt-1 block text-[12.5px] text-attente-encre">Le document ne donnait pas la durée de chaque module : la durée totale a été répartie, à ajuster.</span>}
                    {actuel.nb_modules_actuels > 0 && <span className="mt-0.5 block text-[12.5px] text-encre-3">Remplace les {actuel.nb_modules_actuels} module(s) actuels (l'ancienne version reste dans l'historique après enregistrement).</span>}
                  </span>
                </label>
              </li>
            </ul>
            <div className="flex flex-wrap justify-end gap-2">
              <Bouton variante="discret" onClick={() => setProposition(null)}>
                Ne rien reprendre
              </Bouton>
              <Bouton variante="primaire" disabled={!Object.values(choix).some(Boolean)} onClick={reprendre}>
                Reprendre dans le formulaire
              </Bouton>
            </div>
          </div>
        )}
      </Modale>
    </Carte>
  );
}
